import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { FilePlus2, UploadCloud, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function ResumeEntryGate({ onScratch, onImported }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: extracted } = await api.post("/resume-checker/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: structured } = await api.post("/resume-builder/import-resume-file", { resume_text: extracted.resume_text });
      onImported(structured);
      toast.success("Resume imported — review and edit below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read this file — try a different one or build from scratch");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-3xl mx-auto text-center py-10" data-testid="resume-entry-gate">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">How Do You Want to Start?</span>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white mt-2 mb-2">Pick the fastest path to your resume.</h1>
      <p className="text-slate-400 text-sm mb-8">Choose how you'd like to begin — both take just a few minutes.</p>

      <div className="grid sm:grid-cols-2 gap-5">
        <button
          onClick={onScratch} data-testid="resume-gate-scratch-btn"
          className="glass-panel rounded-2xl p-8 text-left border border-white/10 hover:border-orange-400/40 transition-colors group"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center mb-5">
            <FilePlus2 className="w-6 h-6 text-white" />
          </div>
          <p className="text-lg font-heading font-semibold text-white mb-1.5">Build from scratch</p>
          <p className="text-sm text-slate-400 mb-5">Start with a blank page and let AI write every section with you.</p>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-orange-400 group-hover:gap-2.5 transition-all">
            Continue <ArrowRight className="w-4 h-4" />
          </span>
        </button>

        <button
          onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="resume-gate-upload-btn"
          className="glass-panel rounded-2xl p-8 text-left border border-white/10 hover:border-indigo-400/40 transition-colors group disabled:opacity-70"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center mb-5">
            {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <UploadCloud className="w-6 h-6 text-white" />}
          </div>
          <p className="text-lg font-heading font-semibold text-white mb-1.5">Upload resume</p>
          <p className="text-sm text-slate-400 mb-5">
            {uploading ? "Reading and structuring your resume for AI editing…" : "Drop in your existing resume and we'll structure it for AI editing right away."}
          </p>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-indigo-400 group-hover:gap-2.5 transition-all">
            {uploading ? "Please wait…" : "Continue"} {!uploading && <ArrowRight className="w-4 h-4" />}
          </span>
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="resume-gate-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}
